import CFG from '../gameLoopConfig.json';
import {
  WORLD_WIDTH, WORLD_HEIGHT, PLAYER_SIZE,
} from '../../../constants';
const IS_TOUCH       = navigator.maxTouchPoints > 0;
const POND_SLOW      = CFG.pond.slowFactors;
const LAVA_RARITIES  = new Set(CFG.pond.lavaRarities);

// ── Ponds ──────────────────────────────────────────────────────────────────

export function updatePonds(s, dt, timestamp, terrainDy, setGameState) {
  const p = CFG.terrain.ponds;
  if (s.nextPondAt === 0) {
    s.nextPondAt = timestamp + p.firstDelayMin + Math.random() * p.firstDelayRand;
  }
  if (!s.tierBossAlive && timestamp >= s.nextPondAt) {
    const sz = Math.round((p.sizeMin + Math.floor(Math.random() * p.sizeRange)) * (IS_TOUCH ? p.mobileSizeFactor : 1));
    s.ponds.push({ id: s.nextPondId++, x: Math.random() * (WORLD_WIDTH - sz), y: -(sz + 10), size: sz });
    s.nextPondAt = timestamp + p.subsequentMin + Math.random() * p.subsequentRand;
  }
  for (const pond of s.ponds) pond.y += terrainDy;
  s.ponds = s.ponds.filter(pond => pond.y < WORLD_HEIGHT + pond.size);

  // Player–pond overlap: apply slow + optional lava damage
  const pMod = s.activeModifier;
  let playerInPond = false;
  for (const pond of s.ponds) {
    const ps = pond.size;
    if (s.player.x < pond.x + ps && s.player.x + PLAYER_SIZE > pond.x &&
        s.player.y < pond.y + ps && s.player.y + PLAYER_SIZE > pond.y) {
      playerInPond = true;
      s.pondSlowFactor = POND_SLOW[pMod?.rarity] ?? 0.80;
      if (pMod && LAVA_RARITIES.has(pMod.rarity) &&
          timestamp - (s.pondLastDmgAt ?? -99999) >= CFG.pond.lavaDmgInterval) {
        s.pondLastDmgAt = timestamp;
        s.hp -= CFG.pond.lavaDmgPerTick;
        s.floatingTexts.push({
          id: `ft_lava_${timestamp}`, x: s.player.x + PLAYER_SIZE / 2, y: s.player.y - 10,
          text: `🔥 -${CFG.pond.lavaDmgPerTick}`, color: '#ff5500',
          startTime: timestamp, duration: 900,
        });
        if (s.hp <= 0) { s.alive = false; setGameState('dead'); return true; }
      }
      break;
    }
  }
  if (!playerInPond) s.pondSlowFactor = 1;
  return false;
}

// ── Rocks ──────────────────────────────────────────────────────────────────

export function updateRocks(s, timestamp, terrainDy) {
  const r = CFG.terrain.rocks;
  if (s.nextRockAt === 0) {
    s.nextRockAt = timestamp + r.firstDelayMin + Math.random() * r.firstDelayRand;
  }
  if (!s.tierBossAlive && timestamp >= s.nextRockAt) {
    const size = Math.round((r.sizeMin + Math.floor(Math.random() * r.sizeRange)) * (IS_TOUCH ? r.mobileSizeFactor : 1));
    s.rocks.push({ id: s.nextRockId++, x: Math.random() * (WORLD_WIDTH - size), y: -(size + 10), size });
    s.nextRockAt = timestamp + r.subsequentMin + Math.random() * r.subsequentRand;
  }
  for (const rock of s.rocks) rock.y += terrainDy;
  for (let i = s.rocks.length - 1; i >= 0; i--) {
    if (s.rocks[i].y >= WORLD_HEIGHT + s.rocks[i].size) s.rocks.splice(i, 1);
  }
}

// ── Trees ──────────────────────────────────────────────────────────────────

export function updateTrees(s, timestamp, terrainDy) {
  const t = CFG.terrain.trees;
  if (s.nextTreeAt === 0) {
    s.nextTreeAt = timestamp + t.firstDelayMin + Math.random() * t.firstDelayRand;
  }
  if (!s.tierBossAlive && timestamp >= s.nextTreeAt) {
    const size = t.size;
    s.trees.push({ id: s.nextTreeId++, x: Math.random() * (WORLD_WIDTH - size), y: -(size * 1.4 + 10), size });
    s.nextTreeAt = timestamp + t.subsequentMin + Math.random() * t.subsequentRand;
  }
  for (const tree of s.trees) tree.y += terrainDy;
  for (let i = s.trees.length - 1; i >= 0; i--) {
    if (s.trees[i].y >= WORLD_HEIGHT + s.trees[i].size * 1.4) s.trees.splice(i, 1);
  }
}

// ── World-specific decorative assets ───────────────────────────────────────
// Environment-themed decorations (flowers, tombstones, camels, etc.).
// Purely visual — no collision. Paused during tier boss fight.

export function updateWorldAssets(s, timestamp, terrainDy) {
  if (!s.worldAssets) s.worldAssets = [];
  const wa = CFG.terrain.worldAssets;
  if (s.nextWorldAssetAt === 0) {
    s.nextWorldAssetAt = timestamp + wa.firstDelayMin + Math.random() * wa.firstDelayRand;
  }
  if (!s.tierBossAlive && timestamp >= s.nextWorldAssetAt) {
    const size = wa.size;
    s.worldAssets.push({ id: s.nextWorldAssetId++, x: Math.random() * (WORLD_WIDTH - size), y: -(size + 10), size });
    s.nextWorldAssetAt = timestamp + wa.subsequentMin + Math.random() * wa.subsequentRand;
  }
  for (const a of s.worldAssets) a.y += terrainDy;
  s.worldAssets = s.worldAssets.filter(a => a.y < WORLD_HEIGHT + a.size);
}

// ── Chests ─────────────────────────────────────────────────────────────────

export function updateChests(s, dt, terrainDy) {
  const c = CFG.terrain.chests;
  if ((s.chests ?? []).length < c.maxActive && Math.random() < dt / c.meanSpawnInterval) {
    const cSize = c.size;
    if (!s.chests) s.chests = [];
    s.chests.push({ id: s.nextChestId++, x: cSize + Math.random() * (WORLD_WIDTH - cSize * 2), y: -(cSize + 10), size: cSize });
  }
  for (const chest of (s.chests ?? [])) chest.y += terrainDy;
  s.chests = (s.chests ?? []).filter(ch => ch.y < WORLD_HEIGHT + ch.size);
}
